<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="storePlan">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>{{ discount_code ? 'Update' : 'New' }} Discount Code</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['code']" autofocus :rules="[vRules.required]" density="compact"
                                          label="Code"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['type']" :disabled="!!discount_code"
                                      :items="DiscountCodeTypes" :rules="[vRules.required]" density="compact"
                                      label="Discount Type"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['discount_scheme_id']" :items="discount_schemes"
                                      :loading="!discount_schemes.length" :rules="[vRules.required]" density="compact"
                                      item-title="name" item-value="id" label="Discount Scheme"/>
                        </v-col>

                        <v-col v-if="getCodeType === 'device'" class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['devices']" :items="devices" :loading="!devices.length"
                                      :rules="[vRules.required]" density="compact" item-title="slno" item-value="id"
                                      label="Select Devices" multiple/>
                        </v-col>

                        <v-col v-if="getCodeType === 'product_sku'" class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['product_skus']" :items="product_skus"
                                      :loading="!product_skus.length" :rules="[vRules.required]" density="compact"
                                      item-title="sku" item-value="id" label="Select Product Skus" multiple/>
                        </v-col>

                        <v-col v-if="getCodeType === 'device_group'" class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['device_groups']" :items="device_groups"
                                      :loading="!device_groups.length" :rules="[vRules.required]" density="compact"
                                      item-title="label" item-value="id" label="Select Device Groups" multiple/>
                        </v-col>

                        <v-col v-if="getCodeType === 'company'" class="py-0" cols="12" sm="6">
                            <company-select v-model="formData['companies']" :items="companies" :loading="!companies.length"
                                      :rules="[vRules.required]" density="compact" label="Select Companies" multiple/>
                        </v-col>

                        <v-col v-if="getCodeType === 'subscription_plan'" class="py-0" cols="12" sm="6">
                            <v-select v-model="formData['subscription_plans']" :items="subscription_plans"
                                      :loading="!subscription_plans.length" :rules="[vRules.required]" density="compact"
                                      item-title="name" item-value="id" label="Select Subscription plans" multiple/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['max_allowed_usage']" density="compact"
                                          label="Max Allowed Usage" type="number"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-text-field v-model="formData['max_allowed_usage_per_company']" density="compact"
                                          label="Max Allowed Usage per Company" type="number"/>
                        </v-col>

                        <v-col class="py-0" cols="12" sm="6">
                            <v-date-input
                                v-model="formData['valid_from']"
                                :rules="[vRules.required]"
                                append-inner-icon="$calendar"
                                density="compact"
                                hide-actions
                                label="Start Date"
                                prepend-icon=""
                            />
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-date-input
                                v-model="formData['valid_until']"
                                :rules="[vRules.required]"
                                append-inner-icon="$calendar"
                                density="compact"
                                hide-actions
                                label="End Date"
                                prepend-icon=""
                            />
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-checkbox v-model="formData['requires_new_device']" label="Requires New Device"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-checkbox v-model="formData['requires_new_company']" label="Requires New Company"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-checkbox v-model="formData['auto_apply']" label="Auto Apply"/>
                        </v-col>
                        <v-col class="py-0" cols="12" sm="6">
                            <v-checkbox v-model="formData['is_active']" :rules="[vRules.required]" label="Active"/>
                        </v-col>

                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {send} from "@/composables/apiClient";
import {ref, reactive, onMounted, computed} from "vue";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {VDateInput} from "vuetify/lib/labs/VDateInput";
import CompanySelect from "@/components/globals/company-select.vue";

const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    discount_code: Object
});
const formData = reactive({
    valid: false,
    devices: null,
    companies: null,
    product_skus: null,
    device_groups: null,
    subscription_plans: null,

    code: props.discount_code?.code ?? null,
    type: props.discount_code?.type ?? null,
    max_allowed_usage: props.discount_code?.max_allowed_usage ?? null,
    discount_scheme_id: props.discount_code ? parseInt(props.discount_code?.discount_scheme_id) : null,
    max_allowed_usage_per_company: props.discount_code?.max_allowed_usage_per_company ?? null,
    valid_from: props.discount_code?.valid_from ? new Date(props.discount_code.valid_from) : null,
    valid_until: props.discount_code?.valid_until ? new Date(props.discount_code.valid_until) : null,
    requires_new_company: !!props.discount_code?.requires_new_company ?? false,
    requires_new_device: !!props.discount_code?.requires_new_device ?? false,
    auto_apply: !!props.discount_code?.auto_apply ?? false,
    is_active: !!props.discount_code?.is_active ?? false,

});
const emits = defineEmits(['update:modelValue', 'addDiscountCode']);
const dialogSwitch = ref(props.modelValue);
const subscription_plans = ref([]);
const discount_schemes = ref([]);
const device_groups = ref([]);
const product_skus = ref([]);
const companies = ref([]);
const devices = ref([]);
const errors = ref([]);
const DiscountCodeTypes = [
    {value: 1, title: 'Universal'},
    {value: 2, title: 'Device'},
    {value: 3, title: 'Company'},
    {value: 4, title: 'Product sku'},
    {value: 5, title: 'Device Group'},
    {value: 6, title: 'Subscription Plan'},
];
const getCodeType = computed(() => {
    if (!formData['type']) {
        return null
    }
    const type = DiscountCodeTypes.find((type) => type.value === formData['type']).title.replaceAll(' ', '_').toLowerCase()
    switch (type) {
        case 'device':
            if (!devices.value.length)
                fetchDevicesList()
            break
        case 'company':
            if (!companies.value.length)
                fetchCompaniesList()
            break
        case 'product_sku':
            if (!product_skus.value.length)
                fetchProductSkusList()
            break
        case 'device_group':
            if (!device_groups.value.length)
                fetchDeviceGroupsList()
            break
        case 'subscription_plan':
            if (!subscription_plans.value.length)
                fetchPlansList()
            break
    }

    return type
})

const simplePlural = (noun) => {
    return `${noun !== 'company' ? noun + 's' : 'companies'}`;
}
onMounted(() => {
    fetchDiscountSchemes()
    if (props.discount_code && getCodeType.value !== 'universal') {
        fetchDiscountable()
    }
})
const fetchDevicesList = () => {
    return send('get', `devices-list`)
        .then((response) => {
            devices.value = response.data.data;
        }).catch((error) => {
            snackbar.showSnackbar('Discount schemes list could not be loaded', 'error')
        })
}
const fetchProductSkusList = () => {
    send('get', `product-skus`, {
        list: true,
        columns: JSON.stringify("id,sku")
    })
        .then((response) => {
            product_skus.value = response.data.data.product_skus;
        }).catch((error) => {
        snackbar.showSnackbar('Product skus list could not be loaded', 'error')
    })
}
const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        })
}
const fetchDeviceGroupsList = () => {
    return send('get', `device-groups`, {list: true})
        .then((response) => {
            device_groups.value = response.data.data.device_groups;
        }).catch((error) => {
            snackbar.showSnackbar('Device Groups could not be loaded', 'error')
        })
}
const fetchPlansList = () => {
    send('get', `subscription-plans/list`)
        .then((response) => {
            subscription_plans.value = response.data.data;
        }).catch((error) => {
        snackbar.showSnackbar('Subscription plans List could not be loaded', 'error')
    })
}
const fetchDiscountSchemes = () => {
    return send('get', `admin/discount-schemes`, {list: true})
        .then((response) => {
            discount_schemes.value = response.data.data;
        }).catch((error) => {
            snackbar.showSnackbar('Discount schemes list could not be loaded', 'error')
        })
}
const fetchDiscountable = () => {
    return send('get', `admin/discount-codes/${props.discount_code.id}/discountable`)
        .then((response) => {
            const typeVar = simplePlural(getCodeType.value);
            formData[typeVar] = response.data.data;
        }).catch((error) => {
            console.log(error)
            snackbar.showSnackbar('Discount schemes list could not be loaded', 'error')
        })
}
const storePlan = () => {
    errors.value = [];
    const method = props.discount_code ? 'put' : 'post';
    const endpoint = props.discount_code ? `admin/discount-codes/${props.discount_code.id}` : `admin/discount-codes`;
    send(method, endpoint, formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                if (props.discount_code) {
                    emits('updateDiscountCode', response.data.data);
                } else {
                    emits('addDiscountCode', response.data.data);
                }
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

</script>
